import { checkText } from 'smile2emoji';
import qs from 'qs';
import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import FirebaseService from 'services/FirebaseService';
import GiphyService from 'services/GiphyService';
import { getActiveLanguageCode, translate } from 'services/i18nService';
import createMessagingService from 'services/MessagingService/MessagingService';
import { SET_JWT_TOKEN } from 'services/MessagingService/EventNames';
import NotificationService from 'services/NotificationService';
import { MESSAGE_CONFIG, NOTIFICATION_TYPE } from 'enum/messages';
import parseCatch from 'utils/parseCatch';

import { accessLevelFromShortName, genderFromShortName } from '../firebase/firebaseTranscribers';
import { CHAT_MESSAGE_LABELS } from '../firebase/firebaseStructure';
import firebaseNode from '../firebase/firebaseNode';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);

const TRANSLATION_SETTINGS = `${BASE_URL}/rest/v1.0/translationProfiles/{username}`;
const CHAT_SETTINGS = `${WEBCHAT_HOST}chatSettings`;

class ChatService {
  constructor() {
    this.messagingService = createMessagingService();
    this.api = {
      WEBCHAT_POST_MESSAGE_MAIN_CHAT: `${WEBCHAT_HOST}writeMessage`,
      WEBCHAT_POST_MESSAGE_PRIVATE_CHAT: `${WEBCHAT_HOST}writePrivateMessage`,
      WEBCHAT_POST_MESSAGE_GROUP_SHOW_CHAT: `${WEBCHAT_HOST}writeGroupShowMessage`,
    };
  }

  // remove this once USE_WEBCHAT_CHAT_HISTORY is launched for all users
  watchMainChat = (roomName, callbackFnc) => {
    const mainChatRef = FirebaseService.FirebaseMain.child(
      `${firebaseNode.CHAT_ROOMS}/${roomName.toLowerCase()}/chatMessages`
    );

    // get last n messages from message history
    const historyLimit = window?.abt?.chat?.historyLimit || MESSAGE_CONFIG.HISTORY_LIMIT;

    return mainChatRef.limitToLast(historyLimit).once('value', (snapshot) => callbackFnc(snapshot));
  };

  watchPrivateChat = (roomName, username, callbackFnc) => {
    const privateChatRef = FirebaseService.FirebaseMain.child(
      `${firebaseNode.CHAT_ROOMS}/${roomName}/privateMessages/${username.toLowerCase()}`
    );

    // get last n messages from message history
    return privateChatRef.limitToLast(MESSAGE_CONFIG.HISTORY_LIMIT).once('value', (snapshot) => callbackFnc(snapshot));
  };

  watchGroupShowChat = (roomName, callbackFnc) => {
    const groupShowChatRef = FirebaseService.FirebaseMain.child(
      `${firebaseNode.CHAT_ROOMS}/${roomName.toLowerCase()}/groupShowMessages`
    );

    // get last n messages from message history
    return groupShowChatRef
      .limitToLast(MESSAGE_CONFIG.HISTORY_LIMIT)
      .once('value', (snapshot) => callbackFnc(snapshot));
  };

  sendMainChatMessage(username, accessHash, roomname, message, isGif, gifUrlAnalytics) {
    const messageDataForGif = {
      username,
      accessHash,
      roomname,
      message: '',
      gifId: message,
    };

    const messageData = {
      username,
      accessHash,
      roomname,
      message: checkText(message),
    };

    const headers = { 'content-type': 'application/x-www-form-urlencoded; charset=utf-8' };

    const parsedURL = encodeURI(`${this.api.WEBCHAT_POST_MESSAGE_MAIN_CHAT}`);
    const data = qs.stringify(isGif ? messageDataForGif : messageData);

    return ApiService.post(parsedURL, { data, headers })
      .then((res) => {
        if (isGif) {
          GiphyService.sendAnalytics(gifUrlAnalytics);
        }

        return res;
      })
      .catch(() => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('homepage.v2.errors.generic'),
          subtype: NotificationService.subtype.ERROR,
        });
      });
  }

  sendPrivateChatMessage(username, accessHash, roomName, message, recipient, isGif, gifUrlAnalytics) {
    const messageDataForGif = {
      username,
      recipient: recipient || roomName,
      accessHash,
      roomname: roomName,
      message: '',
      gifId: message,
    };

    const messageData = {
      username,
      recipient: recipient || roomName,
      accessHash,
      roomname: roomName,
      message: checkText(message),
    };

    const headers = { 'content-type': 'application/x-www-form-urlencoded; charset=utf-8' };

    const parsedURL = encodeURI(`${this.api.WEBCHAT_POST_MESSAGE_PRIVATE_CHAT}`);
    const data = qs.stringify(isGif ? messageDataForGif : messageData);

    return ApiService.post(parsedURL, { data, headers })
      .then((res) => {
        if (isGif) {
          GiphyService.sendAnalytics(gifUrlAnalytics);
        }

        return res;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }

  sendGroupShowMessage(username, accessHash, roomname, message, isGif, gifUrlAnalytics) {
    const messageDataForGif = {
      username,
      accessHash,
      roomname,
      message: '',
      gifId: message,
    };

    const messageData = {
      username,
      accessHash,
      roomname,
      message: checkText(message),
    };

    const headers = { 'content-type': 'application/x-www-form-urlencoded; charset=utf-8' };

    const parsedURL = encodeURI(`${this.api.WEBCHAT_POST_MESSAGE_GROUP_SHOW_CHAT}`);
    const data = qs.stringify(isGif ? messageDataForGif : messageData);

    return ApiService.post(parsedURL, { data, headers })
      .then((res) => {
        if (isGif) {
          GiphyService.sendAnalytics(gifUrlAnalytics);
        }

        return res;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }

  parseHistoryMessage = (key, messageObject) => {
    const parsedMessage = {
      messageKey: `${key}${messageObject[CHAT_MESSAGE_LABELS.TIMESTAMP]}`,
      message: messageObject[CHAT_MESSAGE_LABELS.MESSAGE],
      isGif: messageObject[CHAT_MESSAGE_LABELS.GIF_ID] && messageObject[CHAT_MESSAGE_LABELS.GIF_ID] !== '',
      senderOriginalUsername: messageObject[CHAT_MESSAGE_LABELS.ORIGINAL_USERNAME],
      gender: genderFromShortName(messageObject[CHAT_MESSAGE_LABELS.GENDER]),
      language: messageObject[CHAT_MESSAGE_LABELS.LANGUAGE],
      gifId: messageObject[CHAT_MESSAGE_LABELS.GIF_ID],
      originalMessageHash: messageObject[CHAT_MESSAGE_LABELS.MESSAGE_HASH],
      isPrivate: !!messageObject.recipient,
      recipient: messageObject.recipient,
      // Get type of message (if undefined - normal chat message and set to 1,
      // else notification message)
      type:
        typeof messageObject[CHAT_MESSAGE_LABELS.TYPE] === 'undefined' ? 1 : messageObject[CHAT_MESSAGE_LABELS.TYPE],

      // Depending on type of message it has extra info (tokens tipped, gift id, etc)
      notificationExtra:
        messageObject[CHAT_MESSAGE_LABELS.TOKENS] ||
        messageObject[CHAT_MESSAGE_LABELS.GIFT_ID] ||
        messageObject[CHAT_MESSAGE_LABELS.FANCLUB] ||
        messageObject[CHAT_MESSAGE_LABELS.GAME_RESULT],

      gameName: messageObject[CHAT_MESSAGE_LABELS.GAME_NAME],

      // Anonymous setting for tipping notifications
      anonymous: messageObject[CHAT_MESSAGE_LABELS.ANONYMOUS] || 0,
      senderAccessLevel: accessLevelFromShortName(messageObject[CHAT_MESSAGE_LABELS.ACCESS_LEVEL]),
      isFanclubSubscribed: messageObject[CHAT_MESSAGE_LABELS.FANCLUB] === '1',
      isModeratorValue: messageObject[CHAT_MESSAGE_LABELS.IS_ROOM_MODERATOR],
      senderIsRoomModerator: messageObject[CHAT_MESSAGE_LABELS.IS_ROOM_MODERATOR] === '1',

      viewerType: messageObject[CHAT_MESSAGE_LABELS.VIEWER_TYPE] || 0,
      timestamp: messageObject[CHAT_MESSAGE_LABELS.TIMESTAMP] || Date.now(),
      isBroadcasting: messageObject[CHAT_MESSAGE_LABELS.IS_BROADCASTING] === '1',
      isGoalReached: messageObject[CHAT_MESSAGE_LABELS.TIP_TO_REACH_GOAL] === '1',
      isHistoryMessage: true,
      isLiveTouchOn: messageObject.isLiveTouchOn,
      gifUrls: messageObject.gifUrls,
      isExternalMessage: Boolean(messageObject[CHAT_MESSAGE_LABELS.TENANT]),
    };

    if (typeof messageObject[CHAT_MESSAGE_LABELS.TYPE] !== 'undefined') {
      parsedMessage.isGuest = messageObject.g;
    }

    if (parsedMessage.type === NOTIFICATION_TYPE.SEND_TIP) {
      parsedMessage.currency = messageObject[CHAT_MESSAGE_LABELS.CURRENCY];
      parsedMessage.tenant = messageObject[CHAT_MESSAGE_LABELS.TENANT];
    }

    if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_PURCHASE) {
      parsedMessage.videoTitle = messageObject[CHAT_MESSAGE_LABELS.VIDEO_TITLE];
      parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
    }
    if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_BULK_PURCHASE) {
      parsedMessage.videoCount = messageObject[CHAT_MESSAGE_LABELS.VIDEO_COUNT];
      parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
    }
    if (parsedMessage.type === NOTIFICATION_TYPE.GIFT) {
      parsedMessage.giftImage = messageObject[CHAT_MESSAGE_LABELS.GIFT_IMAGE];
      parsedMessage.giftValue = messageObject[CHAT_MESSAGE_LABELS.GIFT_VALUE];
    }

    let messageHash = '';
    if (CHAT_MESSAGE_LABELS.MESSAGE_HASH in messageObject) {
      messageHash = messageObject[CHAT_MESSAGE_LABELS.MESSAGE_HASH];
    }
    if (parsedMessage.type === NOTIFICATION_TYPE.BOOST_ENDED) {
      parsedMessage.boostUniqueViewers = messageObject[CHAT_MESSAGE_LABELS.BOOST_UNIQUE_VIEWERS];
      parsedMessage.boostViewers = messageObject[CHAT_MESSAGE_LABELS.BOOST_VIEWERS];
    }
    parsedMessage.hash = messageHash;

    return parsedMessage;
  };

  parseLightMessage = (messageObject) => {
    const properties = messageObject[CHAT_MESSAGE_LABELS.PROPERTIES];
    const translatorHashStartingIndex = 7;
    const translatorHashLength = 6;
    let parsedMessage = {};

    if (typeof properties !== typeof undefined) {
      const offset = properties.charAt(1) !== '0' && properties.charAt(1) !== '1' ? 1 : 0;
      const firstDelimiter = properties.indexOf('$');
      const secondDelimiter = properties.indexOf('$', firstDelimiter + 1);
      const thirdDelimiter = properties.indexOf('$', secondDelimiter + 1);
      let timestamp = undefined;
      if (thirdDelimiter > 0) {
        timestamp = properties.substring(secondDelimiter + 1, thirdDelimiter);
      } else {
        timestamp = properties.substring(secondDelimiter + 1);
      }
      // regular message
      parsedMessage = {
        message: messageObject[CHAT_MESSAGE_LABELS.MESSAGE],
        isGif: messageObject[CHAT_MESSAGE_LABELS.GIF_ID] && messageObject[CHAT_MESSAGE_LABELS.GIF_ID] !== '',
        senderAccessLevel: accessLevelFromShortName(properties.substring(0, 1 + offset)),
        senderIsRoomModerator: properties.charAt(1 + offset) === '1',
        gifUrls: messageObject.gifUrls,
        isFanclubSubscribed: properties.charAt(2 + offset) === '1',
        gender: genderFromShortName(properties.charAt(3 + offset)),
        language: properties.substring(4 + offset, 6 + offset),
        gifId: messageObject[CHAT_MESSAGE_LABELS.GIF_ID],
        viewerType: properties.charAt(6 + offset),
        originalMessageHash: properties.substring(translatorHashStartingIndex, firstDelimiter),
        hash: properties.substring(translatorHashStartingIndex, translatorHashStartingIndex + translatorHashLength),
        senderOriginalUsername: properties.substring(firstDelimiter + 1, secondDelimiter),
        isBroadcasting: thirdDelimiter > 0 ? properties.charAt(thirdDelimiter + 1) === '1' : 1,
        timestamp,
        type: 1,
        isHistoryMessage: false,
        isExternalMessage: properties.substring(thirdDelimiter + 1).charAt(1) === '1',
      };
    } else {
      // notification
      parsedMessage = {
        senderOriginalUsername: messageObject[CHAT_MESSAGE_LABELS.ORIGINAL_USERNAME],
        type: messageObject[CHAT_MESSAGE_LABELS.TYPE],
        // Depending on type of message it has extra info (tokens tipped, gift id, etc)
        notificationExtra:
          messageObject[CHAT_MESSAGE_LABELS.TOKENS] ||
          messageObject[CHAT_MESSAGE_LABELS.GIFT_ID] ||
          messageObject[CHAT_MESSAGE_LABELS.FANCLUB] ||
          messageObject[CHAT_MESSAGE_LABELS.GAME_RESULT],
        gameName: messageObject[CHAT_MESSAGE_LABELS.GAME_NAME],
        gameOperation: messageObject[CHAT_MESSAGE_LABELS.GAME_OPERATION],
        originalMessageHash: messageObject[CHAT_MESSAGE_LABELS.MESSAGE_HASH],
        // Anonymous setting for tipping notifications
        anonymous: messageObject[CHAT_MESSAGE_LABELS.ANONYMOUS] || 0,
        timestamp: messageObject[CHAT_MESSAGE_LABELS.TIMESTAMP] || Date.now(),
        isGoalReached: messageObject[CHAT_MESSAGE_LABELS.TIP_TO_REACH_GOAL] === '1',
        // Adding access level
        senderAccessLevel: accessLevelFromShortName(messageObject[CHAT_MESSAGE_LABELS.ACCESS_LEVEL]),
        isHistoryMessage: false,
        isLiveTouchOn: messageObject.isLiveTouchOn,
        isGuest: messageObject.g,
      };

      if (parsedMessage.type === NOTIFICATION_TYPE.SEND_TIP) {
        parsedMessage.currency = messageObject[CHAT_MESSAGE_LABELS.CURRENCY];
        parsedMessage.tenant = messageObject[CHAT_MESSAGE_LABELS.TENANT];
      }

      if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_PURCHASE) {
        parsedMessage.videoTitle = messageObject[CHAT_MESSAGE_LABELS.VIDEO_TITLE];
        parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
      }
      if (parsedMessage.type === NOTIFICATION_TYPE.MARKETPLACE_BULK_PURCHASE) {
        parsedMessage.videoCount = messageObject[CHAT_MESSAGE_LABELS.VIDEO_COUNT];
        parsedMessage.videoPrice = messageObject[CHAT_MESSAGE_LABELS.VIDEO_PRICE];
      }
      if (parsedMessage.type === NOTIFICATION_TYPE.GIFT) {
        parsedMessage.giftImage = messageObject[CHAT_MESSAGE_LABELS.GIFT_IMAGE];
        parsedMessage.giftValue = messageObject[CHAT_MESSAGE_LABELS.GIFT_VALUE];
      }
      if (parsedMessage.type === NOTIFICATION_TYPE.BOOST_ENDED) {
        parsedMessage.boostUniqueViewers = messageObject[CHAT_MESSAGE_LABELS.BOOST_UNIQUE_VIEWERS];
        parsedMessage.boostViewers = messageObject[CHAT_MESSAGE_LABELS.BOOST_VIEWERS];
      }
      parsedMessage.isTippingMessage = parsedMessage.type === NOTIFICATION_TYPE.SEND_TIP;
    }

    parsedMessage.messageKey = `chatMessages${parsedMessage.timestamp}`;

    return parsedMessage;
  };

  getTranslationSettings = (username, mainLanguage) => {
    const defaultLanguage = mainLanguage || getActiveLanguageCode();
    let settings = Promise.resolve({ active: false, languageCode: defaultLanguage });
    if (username) {
      const url = ApiService.substitute(TRANSLATION_SETTINGS, { username });
      settings = ApiService.get(encodeURI(url)).then((res) => {
        if (res.status === StatusCodes.OK) {
          return res.data;
        }

        return { active: false, languageCode: defaultLanguage };

      });
    }
    settings = settings.then((s) => window?.abt?.chat?.computeTranslationSettings?.(s) || s);

    return settings;
  };

  saveTranslationSettings = (username, data) => {
    const url = ApiService.substitute(TRANSLATION_SETTINGS, { username });

    return ApiService.post(encodeURI(url), { data });
  };

  saveChatSettings = (data) => {
    const headers = { 'content-type': 'application/x-www-form-urlencoded' };

    return ApiService.post(CHAT_SETTINGS, {
      data: qs.stringify(data),
      headers,
    });
  };

  translateMsg = async(msg, translationSettings) => {
    const newTranslatorUrl = ConfigurationService.get(ConfigurationKeys.TRANSLATOR_URL);
    const params = {
      target: translationSettings?.languageCode || window.LOCALE,
      token: msg.hash,
      q: msg.message,
    };
    const translatorUrl = translationSettings?.url || `${newTranslatorUrl}?`;

    const encodeGetParams = (p) =>
      Object.entries(p)
        .map((kv) => kv.map(encodeURIComponent).join('='))
        .join('&');
    const URL = translatorUrl + encodeGetParams(params);

    const response = await ApiService.get(URL, { withCredentials: false });

    return response.data;
  };

  getChatSettings = (userName) => {
    const params = { userName };

    return ApiService.get(CHAT_SETTINGS, { params });
  };

  changeChatSettings = (data) => {
    const headers = { 'content-type': 'application/x-www-form-urlencoded' };

    return ApiService.post(CHAT_SETTINGS, {
      data: qs.stringify(data),
      headers,
    })
      .then((res) => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.changesSaved'),
          subtype: NotificationService.subtype.SUCCESS,
        });

        if (res.data?.jwtToken) {
          this.messagingService.emit(SET_JWT_TOKEN, res.data.jwtToken);
        }
      })
      .catch((error) => {
        parseCatch(error);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.errorGeneric'),
          subtype: NotificationService.subtype.ERROR,
        });
      });
  };
}

export default new ChatService();
